
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Trash2, RotateCcw, TrendingUp, Package, AlertTriangle, FileText, ShoppingCart } from 'lucide-react';
import { useInventaire, ArticleInventaire } from '@/hooks/useInventaire';
import { toast } from '@/components/ui/use-toast';
import { useTranslation } from 'react-i18next';

interface ActionsEnLotInventaireProps {
  articles: ArticleInventaire[];
  bateauId?: string;
  nomBateau?: string;
  onRefresh?: () => void;
}

const ActionsEnLotInventaire = ({ 
  articles, 
  bateauId, 
  nomBateau,
  onRefresh 
}: ActionsEnLotInventaireProps) => {
  const { 
    supprimerTousArticles, 
    reinitialiserTousLesStocks, 
    remplirTousLesStocks 
  } = useInventaire();
  
  const { t } = useTranslation('inventory');
  const [loading, setLoading] = useState(false);

  // Fonction pour vérifier si un article appartient à une catégorie protégée (équipements/sécurité)
  const estArticleProtege = (article: ArticleInventaire): boolean => {
    const categorie = article.categorie?.toLowerCase() || '';
    return categorie.includes('sécur') || 
           categorie.includes('secur') || 
           categorie.includes('équip') || 
           categorie.includes('equip') ||
           categorie === 'sécurité' || 
           categorie === 'securite' ||
           categorie === 'équipements' || 
           categorie === 'equipements' ||
           categorie === 'équipement' || 
           categorie === 'equipement' ||
           categorie === 'matériel de sécurité' ||
           categorie === 'materiel de securite' ||
           categorie === 'équipement de sécurité' ||
           categorie === 'equipement de securite';
  };

  // Filtrer les articles pour exclure les sections protégées
  const articlesNonProteges = articles.filter(article => !estArticleProtege(article));
  const articlesProteges = articles.filter(article => estArticleProtege(article));

  const handleSupprimerTout = async () => {
    if (!bateauId || articlesNonProteges.length === 0) {
      toast({
        title: t('actionsEnLot.error'),
        description: t('actionsEnLot.noArticleToDelete'),
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const articleIds = articlesNonProteges.map(a => a.id);
      const result = await supprimerTousArticles(articleIds, bateauId);
      
      if (!result.error) {
        if (onRefresh) {
          onRefresh();
        }
        
        if (articlesProteges.length > 0) {
          toast({
            title: t('actionsEnLot.partialDeletionTitle'),
            description: t('actionsEnLot.partialDeletionDescription', { deleted: articlesNonProteges.length, protected: articlesProteges.length }),
          });
        }
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast({
        title: t('actionsEnLot.error'),
        description: t('actionsEnLot.cannotDeleteDescription'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReinitialiserStocks = async () => {
    if (!bateauId || articles.length === 0) {
      toast({
        title: t('actionsEnLot.error'),
        description: t('actionsEnLot.noArticleOrBoat'),
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const articleIds = articles.map(a => a.id);
      const result = await reinitialiserTousLesStocks(articleIds, bateauId);
      
      if (!result.error && onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast({
        title: t('actionsEnLot.error'),
        description: t('actionsEnLot.cannotResetDescription'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemplirStocks = async () => {
    if (!bateauId || articles.length === 0) {
      toast({
        title: t('actionsEnLot.error'),
        description: t('actionsEnLot.noArticleOrBoat'),
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const articleIds = articles.map(a => a.id);
      const result = await remplirTousLesStocks(articleIds, bateauId);
      
      if (!result.error && onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast({
        title: t('actionsEnLot.error'),
        description: t('actionsEnLot.cannotFillDescription'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const articlesVides = articles.filter(a => a.quantite === 0);
  const articlesCritiques = articles.filter(a => {
    const pourcentage = (a.quantite / a.quantite_max) * 100;
    return pourcentage <= 20 && pourcentage > 0;
  });

  if (articles.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5 text-nautic" />
          {t('actionsEnLot.title')}
          {nomBateau && (
            <span className="text-sm font-normal text-muted-foreground ml-2">
              ({nomBateau})
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-nautic-light rounded-lg">
              <Package className="h-4 w-4 text-white" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('actionsEnLot.total')}</p>
              <p className="font-bold text-nautic">{articles.length}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('actionsEnLot.emptyStock')}</p>
              <p className="font-bold text-red-600">{articlesVides.length}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="p-2 bg-orange-100 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('actionsEnLot.criticalStock')}</p>
              <p className="font-bold text-orange-600">{articlesCritiques.length}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="p-2 bg-green-100 rounded-lg">
              <Package className="h-4 w-4 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('actionsEnLot.protected')}</p>
              <p className="font-bold text-green-600">{articlesProteges.length}</p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            onClick={handleRemplirStocks}
            disabled={loading || !bateauId}
            className="bg-green-600 hover:bg-green-700"
          >
            <TrendingUp className="h-4 w-4 mr-2" />
            {t('actionsEnLot.fillAllStocks')}
          </Button>

          <Button
            onClick={handleReinitialiserStocks}
            disabled={loading || !bateauId}
            variant="outline"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            {t('actionsEnLot.resetStocks')}
          </Button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                disabled={loading || !bateauId || articlesNonProteges.length === 0}
                variant="destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {t('actionsEnLot.deleteAll')}
                {articlesProteges.length > 0 && (
                  <Badge variant="outline" className="ml-2 bg-green-100 text-green-800">
                    {t('actionsEnLot.protectedBadge', { count: articlesProteges.length })}
                  </Badge>
                )}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('actionsEnLot.confirmDeletionTitle')}</AlertDialogTitle>
                <AlertDialogDescription className="space-y-2">
                  <p>
                    {t('actionsEnLot.confirmDeletionDescription', { count: articlesNonProteges.length })}
                  </p>
                  {articlesProteges.length > 0 && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-green-800 font-medium flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        {t('actionsEnLot.protectedKeptTitle')}
                      </p>
                      <p className="text-green-700 text-sm mt-1">
                        {t('actionsEnLot.protectedKeptDescription', { count: articlesProteges.length })}
                      </p>
                    </div>
                  )}
                  <p className="text-red-600 font-medium">
                    {t('actionsEnLot.irreversible')}
                  </p>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('actionsEnLot.cancel')}</AlertDialogCancel>
                <AlertDialogAction onClick={handleSupprimerTout} className="bg-red-600 hover:bg-red-700">
                  {t('actionsEnLot.deleteCount', { count: articlesNonProteges.length })}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        {articlesProteges.length > 0 && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 text-sm flex items-center gap-2">
              <Package className="h-4 w-4" />
              <strong>{t('actionsEnLot.protectionActiveTitle')}</strong> {t('actionsEnLot.protectionActiveDescription')}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ActionsEnLotInventaire;
